import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { saveAs } from "file-saver";
import { getCustomers } from "../../api/Customer/customerApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import {
  createSkillAgentProject,
  listSkillAgentProjects,
} from "../../api/Skills/skillAgentProjectsApi";
import {
  matchClientOption,
  optionsFromCustomers,
} from "../Meetings/meetingMoodboardClientOptions";
import { sortTemporadasFromLabel } from "../Meetings/meetingTemporadaSort";
import {
  NEW_COMPASS_PROJECT,
  defaultNewProjectName,
  seasonLabelsForPdfSave,
  validateNewCompassProject,
} from "./muestrasSavePdfProject";
import {
  createSkillAgentClientInfo,
  listSkillAgentClientInfos,
  uploadSkillAgentClientInfoItems,
} from "../../api/Skills/skillAgentClientInfosApi";
import { isPresentacionesRow } from "../Skills/skillAgentClientInfoKind";

const rise = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const softPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(173, 20, 87, 0.28); }
  50% { box-shadow: 0 0 0 10px rgba(173, 20, 87, 0); }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none !important",
  },
};

/**
 * Tras generar el PDF de muestras: elige proyecto Compás y guarda en PRESENTACIONES PDF.
 */
export default function MuestrasSavePdfToCompassDialog({
  open,
  onClose,
  blob = null,
  filename = "Muestras_SDS.pdf",
  suggestedTemporada = "",
  suggestedCustomerId = null,
  onSaved = null,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [catalogSeasons, setCatalogSeasons] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [temporada, setTemporada] = useState("");
  const [projectId, setProjectId] = useState("");
  const [newCustomer, setNewCustomer] = useState(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [titulo, setTitulo] = useState("");
  const [alsoDownload, setAlsoDownload] = useState(true);

  useEffect(() => {
    if (!open) return;
    setError("");
    setNewCustomer(null);
    setNewProjectName("");
    setProjectQuery("");
    setTitulo(String(filename || "").replace(/\.pdf$/i, "") || "Presentación");
    setAlsoDownload(true);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rows, temps, customers] = await Promise.all([
          listSkillAgentProjects(),
          getTemporadas().catch(() => []),
          getCustomers().catch(() => []),
        ]);
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        const options = optionsFromCustomers(customers);
        const catalog = sortTemporadasFromLabel(
          Array.isArray(temps) ? temps : [],
        );
        setProjects(list);
        setCustomerOptions(options);
        setCatalogSeasons(catalog);
        const seasonOptions = seasonLabelsForPdfSave({
          projects: list,
          catalog,
          suggested: suggestedTemporada,
        });
        const preferSeason = String(suggestedTemporada || "").trim();
        const season =
          (preferSeason && seasonOptions.includes(preferSeason)
            ? preferSeason
            : null) ||
          seasonOptions[0] ||
          "";
        setTemporada(season);
        const inSeason = list.filter(
          (p) => String(p.Temporada || "").trim() === season,
        );
        const preferCid = Number(suggestedCustomerId);
        const match = inSeason.find(
          (p) =>
            Number.isFinite(preferCid) &&
            preferCid > 0 &&
            Number(p.CustomerId) === preferCid,
        );
        if (match?.id) {
          setProjectId(String(match.id));
        } else if (inSeason[0]?.id && !(preferCid > 0)) {
          setProjectId(String(inSeason[0].id));
        } else {
          const client = matchClientOption({ customerId: preferCid }, options);
          setProjectId(NEW_COMPASS_PROJECT);
          setNewCustomer(client);
          setNewProjectName(defaultNewProjectName(client, season));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
              t("Samples.savePdfLoadProjectsError", {
                defaultValue: "No se pudieron cargar los proyectos de Compás.",
              }),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, filename, suggestedTemporada, suggestedCustomerId, t]);

  const seasons = useMemo(
    () =>
      seasonLabelsForPdfSave({
        projects,
        catalog: catalogSeasons,
        suggested: suggestedTemporada,
      }),
    [projects, catalogSeasons, suggestedTemporada],
  );

  const projectsInSeason = useMemo(() => {
    const season = String(temporada || "").trim();
    return projects
      .filter((p) => String(p.Temporada || "").trim() === season)
      .sort((a, b) =>
        String(a.CustomerName || a.Name || "").localeCompare(
          String(b.CustomerName || b.Name || ""),
          undefined,
          { sensitivity: "base" },
        ),
      );
  }, [projects, temporada]);

  const creatingProject = projectId === NEW_COMPASS_PROJECT;

  const visibleProjects = useMemo(() => {
    const q = String(projectQuery || "")
      .trim()
      .toLowerCase();
    if (!q) return projectsInSeason;
    return projectsInSeason.filter((p) =>
      `${p.CustomerName || ""} ${p.Name || ""}`.toLowerCase().includes(q),
    );
  }, [projectsInSeason, projectQuery]);

  const leaveNewProject = () => {
    const first = projectsInSeason[0];
    setProjectId(first?.id ? String(first.id) : "");
    setNewCustomer(null);
    setNewProjectName("");
  };

  useEffect(() => {
    if (!open || creatingProject) return;
    if (!projectsInSeason.length) {
      const client = matchClientOption(
        { customerId: Number(suggestedCustomerId) },
        customerOptions,
      );
      setProjectId(NEW_COMPASS_PROJECT);
      setNewCustomer((current) => current || client);
      setNewProjectName((current) =>
        String(current || "").trim()
          ? current
          : defaultNewProjectName(client, temporada),
      );
      return;
    }
    const still = projectsInSeason.some(
      (p) => String(p.id) === String(projectId),
    );
    if (!still) setProjectId(String(projectsInSeason[0].id));
  }, [
    open,
    projectsInSeason,
    projectId,
    creatingProject,
    customerOptions,
    suggestedCustomerId,
    temporada,
  ]);

  const selectedProject = useMemo(
    () => projects.find((p) => String(p.id) === String(projectId)) || null,
    [projects, projectId],
  );

  const startNewProject = (client = newCustomer) => {
    const picked =
      client ||
      matchClientOption(
        { customerId: Number(suggestedCustomerId) },
        customerOptions,
      );
    setProjectId(NEW_COMPASS_PROJECT);
    setNewCustomer(picked);
    setNewProjectName((current) =>
      String(current || "").trim()
        ? current
        : defaultNewProjectName(picked, temporada),
    );
  };

  const handleSave = async () => {
    if (!blob || saving) return;
    const title = String(titulo || "").trim() || "Presentación";
    const pdfName = title.toLowerCase().endsWith(".pdf")
      ? title
      : `${title}.pdf`;
    setSaving(true);
    setError("");
    try {
      let project = selectedProject;
      if (creatingProject) {
        const check = validateNewCompassProject({
          name: newProjectName,
          temporada,
          customer: newCustomer,
        });
        if (!check.ok) {
          const fallback =
            check.error === "client"
              ? "Elige un cliente del catálogo."
              : check.error === "name"
                ? "Pon un nombre al proyecto."
                : "Indica la temporada.";
          const key =
            check.error === "client"
              ? "Samples.savePdfNewClientRequired"
              : check.error === "name"
                ? "Samples.savePdfNewNameRequired"
                : "Samples.savePdfNewSeasonRequired";
          setError(t(key, { defaultValue: fallback }));
          setSaving(false);
          return;
        }
        const created = await createSkillAgentProject(check.body);
        const createdId = Number(created?.id);
        if (!Number.isFinite(createdId) || createdId <= 0) {
          throw new Error(
            t("Samples.savePdfNewProjectError", {
              defaultValue: "No se pudo crear el proyecto.",
            }),
          );
        }
        project = {
          id: createdId,
          Name: check.body.Name,
          Temporada: check.body.Temporada,
          CustomerId: check.body.CustomerId,
          CustomerName: check.body.CustomerName,
        };
      }
      if (!project?.id) return;
      const existing = await listSkillAgentClientInfos({
        projectId: project.id,
      });
      const presentaciones = (Array.isArray(existing) ? existing : []).filter(
        isPresentacionesRow,
      );
      let dossier = presentaciones[0] || null;
      if (!dossier) {
        dossier = await createSkillAgentClientInfo({
          Titulo: project.CustomerName
            ? `Presentaciones PDF · ${project.CustomerName}`
            : "Presentaciones PDF",
          Kind: "presentaciones_pdf",
          CustomerId: project.CustomerId || null,
          CustomerName: project.CustomerName || null,
          Temporada: project.Temporada || temporada || null,
          ProjectId: project.id,
          OrgBrand: "STAGE",
        });
      }
      const file = new File([blob], pdfName, { type: "application/pdf" });
      await uploadSkillAgentClientInfoItems(dossier.id, [file]);
      if (alsoDownload) {
        try {
          saveAs(blob, pdfName);
        } catch {
          /* ignore local download */
        }
      }
      onSaved?.({
        dossierId: dossier.id,
        projectId: project.id,
        filename: pdfName,
      });
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("Samples.savePdfError", {
            defaultValue: "No se pudo guardar el PDF en Compás.",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose?.()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          animation: `${rise} 0.38s ease-out`,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.25,
          pb: 1.5,
          background: `linear-gradient(135deg, ${alpha("#ad1457", 0.12)} 0%, ${alpha(
            "#0277bd",
            0.08,
          )} 100%)`,
          borderBottom: "1px solid",
          borderColor: alpha("#ad1457", 0.16),
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha("#ad1457", 0.14),
              color: "#ad1457",
              animation: `${softPulse} 2.4s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <PictureAsPdfOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="overline"
              sx={{ color: "#ad1457", letterSpacing: 1 }}
            >
              Compás
            </Typography>
            <DialogTitle sx={{ p: 0, fontSize: "1.15rem", fontWeight: 700 }}>
              {t("Samples.savePdfTitle", {
                defaultValue: "Guardar en PRESENTACIONES PDF",
              })}
            </DialogTitle>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("Samples.savePdfLead", {
            defaultValue:
              "Elige el proyecto o crea uno (cliente y nombre). La carpeta de PDFs se crea sola.",
          })}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="save-pdf-season-label">
                {t("Samples.savePdfSeason", { defaultValue: "Temporada" })}
              </InputLabel>
              <Select
                labelId="save-pdf-season-label"
                label={t("Samples.savePdfSeason", {
                  defaultValue: "Temporada",
                })}
                value={temporada}
                onChange={(e) => setTemporada(e.target.value)}
                disabled={saving || !seasons.length}
              >
                {seasons.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.75 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t("Samples.savePdfProject", {
                    defaultValue: "Proyecto Compás",
                  })}
                </Typography>
                <Button
                  size="small"
                  variant={creatingProject ? "text" : "outlined"}
                  startIcon={creatingProject ? null : <AddOutlinedIcon />}
                  onClick={() =>
                    creatingProject ? leaveNewProject() : startNewProject()
                  }
                  disabled={saving}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 1.5,
                    color: "#ad1457",
                    borderColor: alpha("#ad1457", 0.45),
                  }}
                >
                  {creatingProject
                    ? t("Samples.savePdfBackToList", {
                        defaultValue: "Volver al listado",
                      })
                    : t("Samples.savePdfNewProject", {
                        defaultValue: "Crear proyecto",
                      })}
                </Button>
              </Stack>

              {creatingProject ? null : (
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    value={projectQuery}
                    onChange={(e) => setProjectQuery(e.target.value)}
                    disabled={saving || !projectsInSeason.length}
                    placeholder={t("Samples.savePdfProjectSearch", {
                      defaultValue: "Buscar cliente o proyecto",
                    })}
                  />
                  <Box
                    sx={{
                      maxHeight: 240,
                      overflow: "auto",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: alpha("#ad1457", 0.16),
                      bgcolor: alpha("#ad1457", 0.03),
                    }}
                  >
                    {visibleProjects.length ? (
                      visibleProjects.map((p) => {
                        const id = String(p.id);
                        const selected = id === String(projectId);
                        const client =
                          String(p.CustomerName || "").trim() ||
                          t("Samples.savePdfNoClient", {
                            defaultValue: "Sin cliente",
                          });
                        const name =
                          String(p.Name || "").trim() || `Proyecto #${p.id}`;
                        return (
                          <Box
                            key={id}
                            component="button"
                            type="button"
                            onClick={() => setProjectId(id)}
                            disabled={saving}
                            sx={{
                              width: "100%",
                              display: "block",
                              textAlign: "left",
                              font: "inherit",
                              border: 0,
                              borderBottom: "1px solid",
                              borderColor: alpha("#ad1457", 0.08),
                              cursor: "pointer",
                              px: 1.5,
                              py: 1,
                              bgcolor: selected
                                ? alpha("#ad1457", 0.1)
                                : "transparent",
                              boxShadow: selected
                                ? "inset 3px 0 0 #ad1457"
                                : "none",
                              "&:hover": {
                                bgcolor: selected
                                  ? alpha("#ad1457", 0.14)
                                  : alpha("#ad1457", 0.06),
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, lineHeight: 1.25 }}
                            >
                              {client}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", lineHeight: 1.3 }}
                            >
                              {name}
                            </Typography>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ px: 1.5, py: 2 }}
                      >
                        {projectsInSeason.length
                          ? t("Samples.savePdfProjectEmptySearch", {
                              defaultValue: "Ningún proyecto coincide.",
                            })
                          : t("Samples.savePdfProjectEmpty", {
                              defaultValue:
                                "No hay proyectos en esta temporada.",
                            })}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              )}
            </Box>

            {creatingProject ? (
              <>
                <Autocomplete
                  size="small"
                  options={customerOptions}
                  value={newCustomer}
                  onChange={(_e, value) => {
                    setNewCustomer(value);
                    setNewProjectName((current) =>
                      String(current || "").trim()
                        ? current
                        : defaultNewProjectName(value, temporada),
                    );
                  }}
                  getOptionLabel={(opt) => opt?.label || ""}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  disabled={saving}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Samples.savePdfNewClient", {
                        defaultValue: "Cliente",
                      })}
                    />
                  )}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={t("Samples.savePdfNewName", {
                    defaultValue: "Nombre del proyecto",
                  })}
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  disabled={saving}
                />
              </>
            ) : null}

            <TextField
              size="small"
              fullWidth
              label={t("Samples.savePdfFilename", {
                defaultValue: "Nombre del PDF",
              })}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              disabled={saving}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={alsoDownload}
                  onChange={(e) => setAlsoDownload(e.target.checked)}
                  disabled={saving}
                />
              }
              label={t("Samples.savePdfAlsoDownload", {
                defaultValue: "Descargar también en este equipo",
              })}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={() => onClose?.()} disabled={saving}>
          {t("Samples.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            saving ||
            loading ||
            !blob ||
            !projectId ||
            (creatingProject &&
              (!temporada ||
                !newCustomer?.id ||
                !String(newProjectName || "").trim()))
          }
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          sx={{
            bgcolor: "#ad1457",
            "&:hover": { bgcolor: "#880e4f" },
          }}
        >
          {t("Samples.savePdfConfirm", { defaultValue: "Guardar en Compás" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
